/**
 * hooks/useAiUsage.ts
 * Live workspace stats for the sidebar, dashboard and lists. Fetches an initial
 * snapshot then subscribes to the SSE stream (GET /api/hire/v2/workspace/stream)
 * so AI-token usage AND the jobs/candidate counts update INSTANTLY the moment
 * anything changes (add candidate/job, close, activate, match run, consent).
 *
 * One shared EventSource for the whole app (module singleton + subscriber set);
 * every component that calls a hook here re-renders on each push. `rev` bumps on
 * every change so list screens can refetch their rows live.
 *
 * THERE IS NO POLLING. An idle tab makes ZERO background requests. Requests are
 * made only on: app boot (one snapshot), a successful mutation, tab refocus, and
 * a stream reconnect. A timed fallback poll used to sit here and — because the
 * stream was failing silently — became a permanent request every 20s per tab,
 * which is exactly the live-cost problem it was supposed to hedge against.
 *
 * The stream is therefore load-bearing, so every failure is now LOUD: each
 * transition is logged with an `[hire:workspace-stream]` prefix and exposed via
 * `getStreamState()`, instead of vanishing into an empty catch block.
 */
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import axiosInstance from '../api/axiosInstance';
import { HireMatch_Workspace_Url, HireMatch_Workspace_Stream_Url } from '../api/endpoints';
import { registerWorkspaceRefresh, setWorkspaceStreamLive } from '../api/workspaceRefresh';

/** Reconnect backoff. These are CONNECTION attempts, not data polls: they stop
 *  the moment the stream opens, and a healthy stream never schedules one. */
const RECONNECT_MIN_MS = 2_000;
const RECONNECT_MAX_MS = 60_000;
/** After this many consecutive failures we stop entirely and wait for a real
 *  signal (mutation or tab focus). Worst case for a permanently broken stream is
 *  therefore bounded — not one request per minute, forever. */
const MAX_RECONNECT_ATTEMPTS = 5;
/** Floor between focus-driven resyncs. `window.focus` fires on every return to
 *  the page (including from the DevTools panel), so it needs a throttle. */
const FOCUS_REFETCH_MIN_GAP_MS = 60_000;
const LOG = '[hire:workspace-stream]';

export interface AiUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  rerankRuns: number;
}
export interface WorkspaceCounts {
  activeJobs: number;
  draftJobs: number;
  totalJobs: number;
  candidates: number;
  consentExpiring: number;
}
export interface WorkspaceStats {
  aiUsage: AiUsage;
  counts: WorkspaceCounts;
  rev: number;
}

const ZERO: WorkspaceStats = {
  aiUsage: { inputTokens: 0, outputTokens: 0, totalTokens: 0, rerankRuns: 0 },
  counts: { activeJobs: 0, draftJobs: 0, totalJobs: 0, candidates: 0, consentExpiring: 0 },
  rev: 0,
};

/** Where the live stream stands. Exposed for debugging — see `getStreamState()`. */
export type StreamState = 'idle' | 'connecting' | 'open' | 'retrying' | 'dormant' | 'unsupported';

let latest: WorkspaceStats = ZERO;
let started = false;
let source: EventSource | null = null;
let streamState: StreamState = 'idle';
let retryDelay = RECONNECT_MIN_MS;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
/** Consecutive failed connection attempts in the current broken spell. */
let attempts = 0;
/** True once the stream has opened at least once — proves the cookie is valid. */
let everOpened = false;
/** Last focus-driven refetch, to throttle noisy `window.focus` events. */
let lastFocusFetch = 0;
const subs = new Set<(s: WorkspaceStats) => void>();

/**
 * Current stream state, for diagnosing "why aren't my counts live?".
 * In the browser console: `window.__hireStream()`.
 */
export function getStreamState(): StreamState { return streamState; }

function setStreamState(next: StreamState, detail?: string) {
  if (streamState === next) return;
  streamState = next;
  // While the stream is open the server pushes after every mutation, so the
  // post-mutation refetch must stand down — otherwise each POST costs an extra
  // /workspace GET for data already being pushed.
  setWorkspaceStreamLive(next === 'open');
  // Deliberately console-visible in every environment. The whole reason this
  // went unnoticed on staging AND localhost is that the old code failed mute.
  const line = `${LOG} ${next}${detail ? ` — ${detail}` : ''}`;
  if (next === 'open') console.info(line);
  else if (next === 'retrying' || next === 'unsupported') console.warn(line);
  else console.info(line);
}

function publish(s: WorkspaceStats) {
  latest = s;
  subs.forEach((cb) => cb(s));
}

/** Refetch the workspace snapshot and publish it to all subscribers. This is
 *  the fallback path (post-mutation, poll, focus) when SSE didn't deliver. */
function fetchSnapshot(): void {
  axiosInstance.get<WorkspaceStats>(HireMatch_Workspace_Url)
    .then(({ data }) => { if (data) publish(data); })
    .catch(() => { /* keep last-known value */ });
}

/** True while the tab is in the background — no point reconnecting until it's
 *  visible again, and a hidden tab must cost nothing. */
function hidden(): boolean {
  return Platform.OS === 'web' && typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function clearRetry() {
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
}

/** Schedule one reconnect attempt with exponential backoff. */
function scheduleReconnect(why: string) {
  clearRetry();
  if (hidden()) {
    // Hidden tabs wait for `visibilitychange` instead of burning attempts.
    setStreamState('retrying', `${why}; tab hidden, waiting for focus`);
    return;
  }
  // A stream that cannot be established (misconfigured proxy, expired session,
  // wrong API URL) must not turn into an endless background request loop — that
  // is precisely the cost problem we removed. After MAX_ATTEMPTS we go dormant:
  // no timers, no requests. A mutation or a tab refocus revives it.
  attempts += 1;
  if (attempts > MAX_RECONNECT_ATTEMPTS) {
    setStreamState('dormant', `${why}; gave up after ${MAX_RECONNECT_ATTEMPTS} attempts — no further background requests. Counts still update on your own changes and on tab focus.`);
    return;
  }
  setStreamState('retrying', `${why}; attempt ${attempts}/${MAX_RECONNECT_ATTEMPTS} in ${Math.round(retryDelay / 1000)}s`);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    connect();
  }, retryDelay);
  retryDelay = Math.min(RECONNECT_MAX_MS, retryDelay * 2);
}

/**
 * Open (or re-open) the SSE stream. Reconnect is handled here rather than left
 * to EventSource's built-in retry, because a 401 needs the axios refresh flow to
 * run first — EventSource cannot do that itself, and the browser's blind retry
 * would loop on 401 forever.
 */
function connect() {
  if (typeof EventSource === 'undefined') {
    setStreamState('unsupported', 'EventSource is not available in this runtime — counts update on mutation/focus only');
    return;
  }
  // Tear down any previous handle so we never hold two streams open.
  if (source) { try { source.close(); } catch { /* already dead */ } source = null; }

  setStreamState('connecting', HireMatch_Workspace_Stream_Url);
  let es: EventSource;
  try {
    es = new EventSource(HireMatch_Workspace_Stream_Url, { withCredentials: true });
  } catch (e) {
    // Previously swallowed. A bad URL (e.g. HIRE_API_URL not injected at build
    // time) landed here and the app silently fell back to polling forever.
    console.error(`${LOG} could not construct EventSource for ${HireMatch_Workspace_Stream_Url}`, e);
    scheduleReconnect('construction failed');
    return;
  }
  source = es;

  es.onopen = () => {
    // Healthy again → forget the backoff and the failure count. `everOpened`
    // also retires the 401-recovery GET: the cookie is now proven good.
    retryDelay = RECONNECT_MIN_MS;
    attempts = 0;
    everOpened = true;
    setStreamState('open');
  };
  es.onmessage = (event) => {
    try {
      publish(JSON.parse(event.data) as WorkspaceStats);
    } catch (e) {
      console.warn(`${LOG} ignored a malformed frame`, e);
    }
  };
  es.onerror = () => {
    // readyState CLOSED (2) means it will not retry itself; CONNECTING (0) means
    // the browser is already retrying, but we take over so the 401-refresh below
    // always runs first.
    try { es.close(); } catch { /* ignore */ }
    if (source === es) source = null;
    // The recovery GET exists for ONE reason: a 401 needs the axios refresh flow
    // to run before reconnecting. So it is worth spending only while we have
    // never managed to open the stream — that is the case where the cookie is
    // the suspect.
    //
    // Once the stream HAS opened, the cookie is proven and the GET is pure
    // duplication: the server sends the current snapshot as the first frame of
    // every connection (see dashboard.routes.ts), so reconnecting resyncs by
    // itself. A stream that flaps — opens, is cut by a proxy idle timeout,
    // reopens — was costing one /workspace GET per flap, which is what made an
    // untouched screen look like it was polling.
    if (!everOpened && attempts === 0) {
      axiosInstance.get<WorkspaceStats>(HireMatch_Workspace_Url)
        .then(({ data }) => { if (data) publish(data); })
        .catch(() => { /* refresh failed → app routes to login on the next call */ })
        .finally(() => scheduleReconnect('stream error'));
      return;
    }
    scheduleReconnect('stream error');
  };
}

function start() {
  if (started) return;
  started = true;

  // Initial value — the one and only unconditional request.
  fetchSnapshot();

  // Refresh immediately after any successful mutation (add/edit/close/remove
  // job or candidate, match run) — see api/workspaceRefresh + axiosInstance.
  // This is what keeps YOUR OWN actions instant even if the stream is down.
  registerWorkspaceRefresh(fetchSnapshot);

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    /**
     * Returning to the page.
     *
     * `window.focus` is far noisier than "the user came back to the tab": it
     * fires every time focus returns to the page from anywhere else in the
     * browser chrome — including clicking from the DevTools panel back onto the
     * page. Refetching unconditionally here meant idly clicking around while
     * DevTools was open produced a stream of /workspace GETs that looked exactly
     * like a poll.
     *
     * So: if the stream is OPEN there is nothing to do at all — the data is
     * current by definition, that is what the stream is for. Otherwise resync
     * once (throttled) and revive the connection.
     */
    const revive = () => {
      if (streamState === 'open' || streamState === 'connecting') return;
      const now = Date.now();
      if (now - lastFocusFetch >= FOCUS_REFETCH_MIN_GAP_MS) {
        lastFocusFetch = now;
        fetchSnapshot();
      }
      if (streamState !== 'unsupported') {
        retryDelay = RECONNECT_MIN_MS;
        attempts = 0;
        clearRetry();
        connect();
      }
    };
    window.addEventListener('focus', revive);
    if (typeof document !== 'undefined') {
      // Both events fire on a tab switch; `revive` is idempotent and throttled,
      // so the pair cannot produce two requests.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') revive();
      });
    }
    // Console handle for diagnosing a dead stream in any environment.
    (window as unknown as { __hireStream?: () => StreamState }).__hireStream = getStreamState;
  }

  connect();
}

/** Full live workspace stats (counts + AI usage + rev). */
export function useWorkspaceStats(): WorkspaceStats {
  const [stats, setStats] = useState<WorkspaceStats>(latest);
  useEffect(() => {
    start();
    subs.add(setStats);
    setStats(latest); // catch up to anything already streamed
    return () => { subs.delete(setStats); };
  }, []);
  return stats;
}

/** Live AI-token usage. `initial` seeds the first render (e.g. a loaded value). */
export function useAiUsage(initial?: AiUsage): AiUsage {
  const stats = useWorkspaceStats();
  // Prefer the streamed value once it has any data; else the seed.
  if (stats.aiUsage.totalTokens > 0 || !initial) return stats.aiUsage;
  return initial;
}
